import { AsyncPipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
    inject
} from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { IconComponent } from "@components/icon/icon.component";
import { PillboxComponent } from "@components/pillbox/pillbox.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import { Observable, merge } from "rxjs";
import {
    filter,
    map,
    scan,
    switchMap,
    tap
} from "rxjs/operators";
import { SimpleSelectionItem } from "~app/common/components/selector/simple-selection-item";
import { IExternOrganisation } from "~app/common/models/kontakter/KontaktDTO";
import { AvtalService } from "~app/features/virkeskop/services/avtal.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { LeverantorMediatorService } from "~app/features/virkeskop/services/leverantor-mediator.service";

@Component({
    selector: "rask-leverantors-organisationer-popover",
    templateUrl: "./leverantors-organisationer-popover.component.html",
    styleUrls: ["./leverantors-organisationer-popover.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        PillboxComponent,
        IconComponent,
        SimpleSelectorComponent,
        FormsModule,
        ReactiveFormsModule,
        AsyncPipe
    ]
})
export class LeverantorsOrganisationerPopoverComponent implements OnInit {
    @Input() protected control: FormControl;
    protected leverantorsOrganisationer$: Observable<SimpleSelectionItem[]>;
    protected localControl = new FormControl();
    protected selectedLabel: string;

    private readonly avtalService = inject(AvtalService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly leverantorMediatorService = inject(LeverantorMediatorService);

    ngOnInit(): void {
        const leverantorsOrganisation$ =  this.kontraktMediatorService.kontrakt$.pipe(
            map(kontrakt => kontrakt.leverantorsOrganisationExternKod),
            tap(externKod => this.localControl.patchValue(externKod,{ emitEvent: false,
                onlySelf:true })),
            map(() => (options: SimpleSelectionItem[]) => options)
        );

        const leverantorsOrganisationer$ = this.leverantorMediatorService.leverantorer$.pipe(
            filter(lev => lev.some(l => !!l.kontaktperson && !!l.id)),
            map(leverantorer => leverantorer.find(l => l.kontaktperson)),
            switchMap(leverantor => this.avtalService.getLeverantorOrganisationer(leverantor?.id)),
            map(leverantorsOrganisationer => (_: SimpleSelectionItem[]) => this.mapToSimpleSelectionItem(leverantorsOrganisationer))
        );

        const valueChanges$ = this.localControl.valueChanges.pipe(
            tap(() => {
                this.control.markAsDirty();
                this.control.patchValue(this.localControl.value);
            }),
            map(() => (options: SimpleSelectionItem[]) => options)
        );

        this.leverantorsOrganisationer$ = merge(leverantorsOrganisation$, leverantorsOrganisationer$, valueChanges$).pipe(
            scan((
                acc: SimpleSelectionItem[],
                mutatorFn: (options: SimpleSelectionItem[]) => SimpleSelectionItem[]) => mutatorFn(acc),
            []),
            tap(options => {
                const selected = options.find(x => x.value === this.localControl.value);
                this.selectedLabel = `Lev.org: ${selected?.text ?? "Välj"}`;
            })
        );
    }

    private mapToSimpleSelectionItem(leverantorsOrganisationer: IExternOrganisation[]): SimpleSelectionItem[] {
        return leverantorsOrganisationer.map(lo => ({
            value: lo.externKod,
            text: lo.namn
        }));
    }
}
